<template>
  <div class="settings-users-tab">
    <div class="d-flex justify-content-between align-items-center mt-3 mb-4">
      <v-text-field
        v-model="params.query"
        :placeholder="$t('pages.settings.users.searchPlaceholder')"
        class="search-field"
        outlined
        dense
        background-color="white"
        clearable
        hide-details
        autocomplete="off"
        name="usersTabSearch"
      >
        <template #prepend-inner>
          <v-icon>{{ icons.mdiMagnify }}</v-icon>
        </template>
      </v-text-field>

      <v-btn color="primary" @click="onClickAdd">
        <v-icon left>
          {{ icons.mdiPlus }}
        </v-icon>
        {{ $t('pages.settings.users.addUser') }}
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="tableData"
      :loading="loading"
      :no-data-text="$t('pages.settings.users.empty')"
      :items-per-page="-1"
      hide-default-footer
      disable-sort
    >
      <template #[`item.name`]="{ item }">
        {{ item.name && item.name.trim() ? item.name : '-' }}
      </template>

      <template #[`item.created`]="{ item }">
        {{ formatDate(item.created) }}
      </template>

      <template #[`item.validDate`]="{ item }">
        {{ formatDate(item.validDate) }}
      </template>

      <template #[`item.lastSeenAt`]="{ item }">
        {{ formatDate(item.lastSeenAt, true) }}
      </template>

      <template #[`item.selectedIdea`]="{ item }">
        <span
          v-if="item.selectedIdea"
          class="d-inline-block text-truncate"
          :title="item.selectedIdea"
          style="max-width: 150px"
        >
          {{ item.selectedIdea }}
        </span>
        <span v-else>-</span>
      </template>

      <template #[`item.progress`]="{ item }">
        <el-progress
          v-if="item.selectedIdea"
          :percentage="item.selectedIdeaProgress || 0"
          :stroke-width="8"
          :color="progressBarCustomColorMethod"
        />
        <span v-else>-</span>
      </template>

      <template #[`item.ideas`]="{ item }">
        {{ item.ideasCreated }} / {{ item.maxIdeas }}
      </template>

      <template #[`item.actions`]="{ item }">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="onClickEdit(item)"
            >
              <v-icon small>
                {{ icons.mdiPencil }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('edit') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="error"
              v-bind="attrs"
              v-on="on"
              @click="onClickDelete(item)"
            >
              <v-icon small>
                {{ icons.mdiDeleteOutline }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('delete') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <div
      v-if="pagination && pagination.total > 0"
      class="pagination-wrapper p-3"
    >
      <v-pagination
        v-model="params.page"
        :length="totalPages"
        :disabled="loading"
        :total-visible="11"
      />
    </div>

    <dialog-add-user
      v-model="dialogAddUserVisible"
      @close="dialogAddUserVisible = false"
      @finished="onUserSaved"
    />

    <dialog-edit-user
      v-model="dialogEditUserVisible"
      :user="selectedUser"
      @close="dialogEditUserVisible = false"
      @finished="onUserSaved"
    />

    <dialog-delete-user
      v-model="dialogDeleteUserVisible"
      :user="selectedUser"
      @close="dialogDeleteUserVisible = false"
      @finished="onUserSaved"
    />
  </div>
</template>

<script>
import { mdiDeleteOutline, mdiMagnify, mdiPencil, mdiPlus } from '@mdi/js'
import DialogAddUser from '@/views/Settings/components/Dialogs/DialogAddUser'
import DialogDeleteUser from '@/views/Settings/components/Dialogs/DialogDeleteUser'
import DialogEditUser from '@/views/Settings/components/Dialogs/DialogEditUser'
import MixinProgressBar from '@/mixins/progressBar'
import _debounce from 'lodash/debounce'
import axios from 'axios'
import { formatDate } from '@/helpers/dateHelper'

const CancelToken = axios.CancelToken
let source = null

export default {
  name: 'UsersTab',

  components: {
    DialogAddUser,
    DialogEditUser,
    DialogDeleteUser
  },

  mixins: [MixinProgressBar],

  data () {
    return {
      loading: false,
      params: {
        page: 1,
        query: ''
      },
      tableData: [],
      pagination: { page: 1, size: 20, from: 0, to: 0, total: 0 },
      icons: { mdiDeleteOutline, mdiMagnify, mdiPencil, mdiPlus },
      dialogAddUserVisible: false,
      dialogEditUserVisible: false,
      dialogDeleteUserVisible: false,
      selectedUser: null
    }
  },

  computed: {
    headers () {
      const t = key => this.$t(`pages.settings.users.columns.${key}`)

      return [
        { text: t('id'), value: 'id', width: 80 },
        { text: t('email'), value: 'email', width: 260 },
        { text: t('name'), value: 'name', width: 140 },
        // { text: t('plan'), value: 'plan', width: 120 },
        { text: t('selectedIdea'), value: 'selectedIdea', width: 160 },
        { text: t('progress'), value: 'progress', width: 140 },
        { text: t('ideas'), value: 'ideas', width: 90, align: 'center' },
        { text: t('created'), value: 'created', width: 120 },
        { text: t('lastSeen'), value: 'lastSeenAt', width: 120 },
        { text: t('sessions'), value: 'sessionsCount', width: 90, align: 'center' },
        { text: t('validUntil'), value: 'validDate', width: 120 },
        {
          text: t('actions'),
          value: 'actions',
          align: 'center',
          divider: false,
          width: 90,
          class: 'sticky-actions',
          cellClass: 'sticky-actions'
        }
      ]
    },

    totalPages () {
      return this.pagination.total
        ? Math.ceil(this.pagination.total / this.pagination.size)
        : 0
    }
  },

  watch: {
    'params.page' () {
      this.getUsers()
    },
    'params.query': {
      handler: _debounce(function () {
        this.onSearch()
      }, 300)
    }
  },

  created () {
    this.getUsers()
  },

  methods: {
    formatDate,

    onSearch () {
      this.params.page = 1
      this.getUsers()
    },

    onClickAdd () {
      this.dialogAddUserVisible = true
    },

    onClickEdit (item) {
      this.selectedUser = item
      this.dialogEditUserVisible = true
    },

    onClickDelete (item) {
      this.selectedUser = item
      this.dialogDeleteUserVisible = true
    },

    onUserSaved () {
      this.getUsers()
    },

    async getUsers () {
      if (source) {
        await source.cancel()
      }
      source = CancelToken.source()

      this.loading = true
      this.$http.get('admin/settings/users', { params: this.params, cancelToken: source.token })
        .then(response => {
          const { data, pagination } = response.data.payload
          this.tableData = data
          this.pagination = pagination
        })
        .catch(error => {
          if (axios.isCancel(error)) return
          this.$notify({
            title: this.$t('error'),
            message: this.$t('pages.settings.users.fetchFailed'),
            type: 'error'
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.settings-users-tab {
  margin-top: 24px;

  ::v-deep .v-data-table td {
    white-space: nowrap;
  }

  ::v-deep .v-data-table {
    .sticky-actions {
      position: sticky;
      right: 0;
      z-index: 1;
      background-color: $color-white;
      box-shadow: -2px 0 4px rgba(0, 0, 0, 0.08);
    }

    th.sticky-actions {
      z-index: 2;
    }

    tbody tr:hover .sticky-actions {
      background-color: #eeeeee;
    }
  }

  .search-field {
    max-width: 320px;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $color-white;
    border-top: 1px solid #ebeef5;

    ::v-deep {
      .v-pagination__item,
      .v-pagination__navigation {
        box-shadow: none;
      }
    }
  }
}
</style>
